// Helpers for reading the data the backend returns from /api/faults.
// The report pages and the landing-page preview both use these, so the preview
// always shows the same fields, wording and calculations as a real report.

// ---------- Registration & mileage ----------

export function normaliseReg(input = "") {
  return String(input).toUpperCase().replace(/[^A-Z0-9]/g, "");
}

// Accepts every UK format (AB12 CDE, A123 BCD, ABC 123D, dateless and private
// plates). The DVSA lookup is the real test of whether a plate exists.
export function isValidReg(input) {
  return /^(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]{2,8}$/.test(normaliseReg(input));
}

export function formatReg(input) {
  const reg = normaliseReg(input);
  if (/^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(reg)) return `${reg.slice(0, 4)} ${reg.slice(4)}`;
  if (/^[A-Z][0-9]{1,3}[A-Z]{3}$/.test(reg)) return `${reg.slice(0, -3)} ${reg.slice(-3)}`;
  if (/^[A-Z]{3}[0-9]{1,3}[A-Z]$/.test(reg)) return `${reg.slice(0, 3)} ${reg.slice(3)}`;
  return reg;
}

export function parseMileage(input) {
  const digits = String(input ?? "").replace(/[^0-9]/g, "");
  return digits ? Number(digits) : NaN;
}

export function isValidMileage(input) {
  const miles = parseMileage(input);
  return Number.isFinite(miles) && miles > 0 && miles <= 400000;
}

// ---------- Formatting ----------

export function formatNumber(value) {
  return Number(value).toLocaleString("en-GB");
}

export function formatDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// "£800-1200" -> "£800–£1,200". Anything that isn't a plain amount or range is left alone.
export function formatCost(cost) {
  if (cost === null || cost === undefined || cost === "") return null;
  const text = String(cost).trim();
  const toAmount = (digits) => `£${formatNumber(Number(digits.replace(/,/g, "")))}`;
  const range = text.match(/^£?\s*([\d,]+)\s*[-–]\s*£?\s*([\d,]+)$/);
  if (range) return `${toAmount(range[1])}–${toAmount(range[2])}`;
  const single = text.match(/^£?\s*([\d,]+)$/);
  if (single) return toAmount(single[1]);
  return text;
}

export function stringList(value) {
  return Array.isArray(value) ? value.filter((item) => typeof item === "string" && item.trim()) : [];
}

// ---------- Vehicle details ----------

export function vehicleYear(info = {}) {
  const date = info.firstUsedDate || info.registrationDate || info.manufactureDate;
  const year = date ? new Date(date).getFullYear() : NaN;
  return Number.isNaN(year) ? null : year;
}

export function vehicleSummaryLine(info = {}) {
  return [
    vehicleYear(info),
    info.fuelType,
    info.engineSize && `${info.engineSize} cc`,
    info.primaryColour,
  ]
    .filter(Boolean)
    .join(" · ");
}

// The DVSA returns "Yes", "No", "Unknown" or "Unavailable".
export function recallStatus(value) {
  if (value === true || /^yes$/i.test(value)) return { tone: "critical", label: "Outstanding recall" };
  if (value === false || /^no$/i.test(value)) return { tone: "good", label: "No outstanding recalls" };
  return { tone: "neutral", label: "Recall status unknown" };
}

// ---------- MOT history ----------

// The DVSA returns "PASSED" / "FAILED"; older data used "PASS" / "FAIL".
export function isPass(test) {
  return /^pass/i.test(test?.testResult || "");
}

export function isFail(test) {
  return /^fail/i.test(test?.testResult || "");
}

export function sortTests(tests = []) {
  return [...tests].sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate));
}

export function groupDefects(defects = []) {
  const groups = { failures: [], repaired: [], minor: [], advisories: [] };
  (defects || []).forEach((defect) => {
    const type = String(defect.type || "").toUpperCase();
    if (defect.dangerous || ["DANGEROUS", "MAJOR", "FAIL"].includes(type)) groups.failures.push(defect);
    else if (type === "PRS") groups.repaired.push(defect);
    else if (type === "MINOR") groups.minor.push(defect);
    else groups.advisories.push(defect);
  });
  return groups;
}

function odometerValue(test) {
  return Number(String(test?.odometerValue ?? "").replace(/[^0-9]/g, "")) || 0;
}

// Mileage in miles, or null when the odometer wasn't read.
export function testMiles(test) {
  if (test?.odometerResultType && test.odometerResultType !== "READ") return null;
  const value = odometerValue(test);
  if (!value) return null;
  return /^km$/i.test(test.odometerUnit || "") ? Math.round(value * 0.621371) : value;
}

export function formatOdometer(test) {
  if (test?.odometerResultType === "UNREADABLE") return "Odometer unreadable";
  if (test?.odometerResultType === "NO_ODOMETER") return "No odometer";
  const value = odometerValue(test);
  if (!value) return "Mileage not recorded";
  return `${formatNumber(value)} ${/^km$/i.test(test.odometerUnit || "") ? "km" : "miles"}`;
}

function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function summariseMot(tests = []) {
  const sorted = sortTests(tests);
  const latest = sorted[0] || null;
  const latestPass = sorted.find(isPass) || null;
  const expiry = latestPass?.expiryDate ? new Date(latestPass.expiryDate) : null;

  return {
    tests: sorted,
    total: sorted.length,
    passes: sorted.filter(isPass).length,
    fails: sorted.filter(isFail).length,
    advisoryCount: sorted.reduce((count, test) => count + groupDefects(test.defects).advisories.length, 0),
    latest,
    latestDefects: latest ? groupDefects(latest.defects) : null,
    expiry,
    expired: expiry ? expiry < startOfToday() : false,
  };
}

// Readings oldest first, with any reading lower than an earlier one flagged.
export function analyseMileage(tests = [], enteredMileage) {
  const readings = sortTests(tests)
    .reverse()
    .map((test) => ({ date: new Date(test.completedDate), miles: testMiles(test), passed: isPass(test) }))
    .filter((reading) => reading.miles !== null && !Number.isNaN(reading.date.getTime()));

  const drops = [];
  let highest = null;
  readings.forEach((reading) => {
    if (highest && reading.miles < highest.miles) {
      reading.dropped = true;
      drops.push({ reading, previous: highest });
    } else {
      highest = reading;
    }
  });

  const first = readings[0];
  const last = readings[readings.length - 1];
  const years = first && last ? (last.date - first.date) / (365.25 * 24 * 60 * 60 * 1000) : 0;
  const entered = parseMileage(enteredMileage);

  return {
    readings,
    drops,
    latest: last || null,
    highest,
    perYear: years >= 1 ? Math.round((last.miles - first.miles) / years / 100) * 100 : null,
    entered: Number.isFinite(entered) ? entered : null,
  };
}

// ---------- Faults & scores ----------

export function sortFaults(faults = []) {
  return [...faults].sort((a, b) => (Number(b.riskFactor) || 0) - (Number(a.riskFactor) || 0));
}

export function faultCost(fault) {
  return formatCost(fault.costEstimate ?? fault.price);
}

export function likelihoodTone(value) {
  const level = String(value || "").toLowerCase();
  if (level === "high") return "critical";
  if (level === "medium") return "warning";
  if (level === "low") return "good";
  return null;
}

// Same thresholds the original results page used.
export function riskBand(score) {
  if (score <= 30) return { label: "Low risk", tone: "good" };
  if (score <= 60) return { label: "Moderate risk", tone: "warning" };
  return { label: "High risk", tone: "critical" };
}

// Makes the /api/faults response safe to render. Handles both backend versions:
// the newer one sends vehicleInfo + motHistory, the older one sends vehicleDetails
// with motHistory inside it. When the AI step fails the backend still sends
// placeholder scores, so those are flagged rather than shown.
export function normaliseReport(data = {}) {
  const details = data.vehicleDetails || {};
  const vehicleInfo = data.vehicleInfo || details;
  const motHistory = data.motHistory || details.motHistory;
  const score = Number(data.riskScore);
  return {
    faults: Array.isArray(data.faults) ? data.faults : [],
    summary: data.summary || {},
    riskScore: data.riskScore !== undefined && Number.isFinite(score) ? Math.max(0, Math.min(100, Math.round(score))) : null,
    kpis: data.kpis || null,
    motHistory: Array.isArray(motHistory) ? motHistory : [],
    vehicleInfo,
    analysisFailed: Boolean(data.error),
  };
}
